import {useCallback, useState} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {message} from "antd";
import {KycDocumentField, UploadKycDocumentsResult} from "../domain/kyc.types";
import {CreateKycDocumentUploadStepFormValues} from "../../../kyc/useCases/create-kyc/multi-step/document-upload/schema-validation";
import {useUploadKycDocuments} from "./useUploadKycDocuments";
import {KYC_QUERY_KEYS} from "./useKycStatus";
import {PROFILE_STATUS_QUERY_KEY} from "../../profile-status/hooks";
import {createKycRepository} from "../data/repositories";

export interface UploadKycDocsOnlyParams {
  /** Document form data from Step 2 (File objects) */
  documentFormData: CreateKycDocumentUploadStepFormValues;
}

export interface UseUploadKycDocsOnlyReturn {
  /** Execute the upload flow. Backend attaches docs to user's existing KYC record. */
  upload: (params: UploadKycDocsOnlyParams) => Promise<UploadKycDocumentsResult>;
  /** Whether upload is in progress */
  isUploading: boolean;
  /** Error from the last operation */
  error: Error | null;
  /** Reset error state */
  resetError: () => void;
}

/**
 * Maps document form field names to domain field names.
 */
const FORM_TO_DOMAIN_FIELD: Record<string, KycDocumentField> = {
  proofOfAddress: "proofOfAddress",
  governmentId: "governmentId",
  selfieWithId: "selfieWithId",
  businessRegistration: "businessRegistration",
};

/**
 * Extracts File objects from document form data.
 * Returns only fields that contain new File objects (not existing URLs).
 */
function extractNewFiles(
  formData: CreateKycDocumentUploadStepFormValues
): Partial<Record<KycDocumentField, File>> {
  const files: Partial<Record<KycDocumentField, File>> = {};

  for (const [key, value] of Object.entries(formData)) {
    const domainField = FORM_TO_DOMAIN_FIELD[key];
    if (domainField && value instanceof File) {
      files[domainField] = value;
    }
  }

  return files;
}

/**
 * Simplified upload-only hook for v2 KYC Step 2.
 *
 * In v2 architecture:
 * - Step 1 creates/updates the KYC record (basic details)
 * - Step 2 only uploads documents (backend attaches to user's KYC record by user ID)
 *
 * This hook:
 * - Extracts File objects from form data
 * - Calls POST /kyc/upload-documents
 * - On success: invalidates KYC status query to refetch
 * - On error: shows error message, allows retry
 */
export function useUploadKycDocsOnly(): UseUploadKycDocsOnlyReturn {
  const queryClient = useQueryClient();
  const uploadMutation = useUploadKycDocuments();
  const [error, setError] = useState<Error | null>(null);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);

  const resetError = useCallback(() => setError(null), []);

  const upload = useCallback(
    async (params: UploadKycDocsOnlyParams): Promise<UploadKycDocumentsResult> => {
      const {documentFormData} = params;
      setError(null);

      const repository = createKycRepository();

      // Extract File objects from form data
      const newFiles = extractNewFiles(documentFormData);
      const hasNewFiles = Object.keys(newFiles).length > 0;

      try {
        let result: UploadKycDocumentsResult = {};

        if (hasNewFiles) {
          result = await uploadMutation.mutateAsync({files: newFiles});
        }

        // Always submit for review after upload (or when docs already exist)
        setIsSubmittingReview(true);
        await repository.submitForReview();
        setIsSubmittingReview(false);

        // Invalidate KYC status to refetch with updated status
        await queryClient.invalidateQueries({queryKey: KYC_QUERY_KEYS.status()});
        // Invalidate profile status so kycVerified flag refreshes
        await queryClient.invalidateQueries({queryKey: PROFILE_STATUS_QUERY_KEY});

        return result;
      } catch (err) {
        setIsSubmittingReview(false);
        const uploadError =
          err instanceof Error ? err : new Error("Failed to submit KYC for review");
        setError(uploadError);
        throw uploadError;
      }
    },
    [uploadMutation, queryClient]
  );

  return {
    upload,
    isUploading: uploadMutation.isLoading || isSubmittingReview,
    error,
    resetError,
  };
}
